import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useIsMobile } from '../../hooks/useMediaQuery';
import {
  BRAND_PRIMARY,
  BRAND_GRADIENT_135,
  BRAND_GRADIENT_135_HOVER,
  BRAND_PRIMARY_10,
  BRAND_PRIMARY_15,
  BRAND_PRIMARY_20,
  BRAND_PRIMARY_30,
  BRAND_SECONDARY_15,
} from '../../constants/brandColors';

const TOOLTIP_STORAGE_KEY = 'ai_workflow_tooltips_dismissed';

const WORKFLOW_TIPS = [
  {
    id: 'welcome',
    title: 'Welcome to your AI Assistant',
    description: 'I can help with notes, scheduling, client questions, and more. Just ask!',
    position: 'top-left',
  },
  {
    id: 'describe-session',
    title: 'Describe what you need',
    description: 'Tell me about a session, ask a question, or request help with any task.',
    position: 'center',
  },
  {
    id: 'review-note',
    title: 'Review and approve',
    description: 'When I generate content, review it, make edits, and approve when ready.',
    position: 'bottom-right',
  },
];

function AIWorkflowTooltips({ user, variant = 'banner' }) {
  const isMobile = useIsMobile();
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  
  const userId = user?.uuid || user?.id || 'anonymous';
  const storageKey = `${TOOLTIP_STORAGE_KEY}_${userId}`;
  
  useEffect(() => {
    if (!user) return;
    
    try {
      const dismissed = localStorage.getItem(storageKey);
      if (!dismissed) {
        setVisible(true);
      }
    } catch (e) {
      // If localStorage fails, show tooltips anyway for first-time experience
      setVisible(true);
    }
  }, [user, storageKey]);
  
  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (e) {
      // localStorage save failed - tooltip will show again next visit
    }
    setVisible(false);
  };
  
  const handleNext = () => {
    if (currentTipIndex < WORKFLOW_TIPS.length - 1) {
      setCurrentTipIndex(currentTipIndex + 1);
    } else {
      handleDismiss();
    }
  };
  
  const handlePrevious = () => {
    if (currentTipIndex > 0) {
      setCurrentTipIndex(currentTipIndex - 1);
    }
  };
  
  if (!visible || !user) {
    return null;
  }
  
  const currentTip = WORKFLOW_TIPS[currentTipIndex];
  const isLastTip = currentTipIndex === WORKFLOW_TIPS.length - 1;
  
  // Banner variant - shows at top of the page
  if (variant === 'banner') {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 1 : 2,
          p: isMobile ? 1.5 : 2,
          mb: isMobile ? 1 : 2,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${BRAND_PRIMARY_15} 0%, ${BRAND_SECONDARY_15} 100%)`,
          border: '1px solid',
          borderColor: BRAND_PRIMARY_30,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          <Box
            sx={{
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              borderRadius: '50%',
              background: BRAND_GRADIENT_135,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <LightbulbOutlinedIcon sx={{ color: 'white', fontSize: isMobile ? 16 : 20 }} />
          </Box>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant={isMobile ? "body2" : "subtitle2"} sx={{ fontWeight: 600, color: BRAND_PRIMARY }}>
              {currentTip.title}
            </Typography>
            <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ display: 'block' }}>
              {currentTip.description}
            </Typography>
          </Box>
          
          {isMobile && (
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{ color: 'text.secondary', p: 0.5 }}
             aria-label="Close">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
          <Typography variant="caption" color="text.secondary">
            {currentTipIndex + 1}/{WORKFLOW_TIPS.length}
          </Typography>
          
          {currentTipIndex > 0 && (
            <Button
              size="small"
              onClick={handlePrevious}
              sx={{ minWidth: 'auto', color: 'text.secondary', px: isMobile ? 1 : 2 }}
            >
              Back
            </Button>
          )}
          
          <Button
            size="small"
            variant="contained"
            onClick={handleNext}
            endIcon={!isLastTip && <ArrowForwardIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
            sx={{
              background: BRAND_GRADIENT_135,
              textTransform: 'none',
              fontWeight: 600,
              px: isMobile ? 1.5 : 2,
              py: isMobile ? 0.5 : 1,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              '&:hover': {
                background: BRAND_GRADIENT_135_HOVER,
              },
            }}
          >
            {isLastTip ? 'Got it!' : 'Next'}
          </Button>
          
          {!isMobile && (
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{ color: 'text.secondary' }}
             aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Paper>
    );
  }
  
  // Compact variant - smaller inline tips
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        backgroundColor: BRAND_PRIMARY_10,
        border: `1px solid ${BRAND_PRIMARY_20}`,
      }}
    >
      <LightbulbOutlinedIcon sx={{ color: BRAND_PRIMARY, fontSize: 18 }} />
      <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
        <strong style={{ color: BRAND_PRIMARY }}>Tip:</strong> {currentTip.description}
      </Typography>
      <IconButton size="small" onClick={handleDismiss} sx={{ color: 'text.secondary' }} aria-label="Close">
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

export default AIWorkflowTooltips;
